<!-- Check out on BeanHub: the handoff. BeanHub has no cart API or add-to-cart
     link, so this opens each coffee's own page on beanhub.com.au for the person
     to add it to his cart, one at a time, and keeps track of which are done.
     Owned by the Bag-tab agent. -->
<template>
  <IonModal
    :is-open="open"
    :breakpoints="[0, 0.72, 1]"
    :initial-breakpoint="0.72"
    class="handoff"
    @didDismiss="onDismiss"
  >
    <div class="handoff__sheet" ref="root">
      <div class="handoff__head">
        <div>
          <p class="handoff__kick">{{ lines.length }} {{ lines.length === 1 ? 'coffee' : 'coffees' }} · {{ total }}</p>
          <h2 class="handoff__title">Check out on BeanHub</h2>
        </div>
        <IonButton fill="clear" class="handoff__close" aria-label="Close" @click="$emit('close')">
          <IonIcon slot="icon-only" :icon="closeOutline" aria-hidden="true" />
        </IonButton>
      </div>
      <p class="handoff__sub">
        BeanHub's checkout happens on its own site, so this opens each coffee there for you to add to your cart.
      </p>

      <div class="handoff__lead">
        <template v-if="allDone">
          <div class="handoff__done" role="status">
            <IonIcon :icon="checkmarkCircle" aria-hidden="true" />
            <p>All done. Finish checkout in BeanHub's cart.</p>
          </div>
          <IonButton expand="block" class="handoff__primary" @click="openCart.run()">
            Go to BeanHub's cart
            <IonIcon slot="end" :icon="openOutline" aria-hidden="true" />
          </IonButton>
        </template>
        <template v-else-if="walking && !nativeWalk">
          <IonButton expand="block" class="handoff__primary" :disabled="openLine.busy.value" @click="openNext">
            Open next ({{ nextIndex + 1 }} of {{ lines.length }})
          </IonButton>
          <p class="handoff__next">Next: {{ nextLine.coffee.name }}</p>
          <IonButton fill="clear" size="small" class="handoff__stop" @click="walking = false">Stop</IonButton>
        </template>
        <template v-else-if="walking">
          <p class="handoff__walking" role="status">
            Close BeanHub's page to go on to {{ nextLine.coffee.name }}.
          </p>
          <IonButton fill="clear" size="small" class="handoff__stop" @click="walking = false">Stop</IonButton>
        </template>
        <IonButton v-else expand="block" class="handoff__primary" @click="startWalk">
          {{ openedCount ? `Open the other ${lines.length - openedCount} on BeanHub` : `Open ${lines.length === 1 ? 'it' : `all ${lines.length}`} on BeanHub, in order` }}
        </IonButton>
        <p v-if="openLine.error.value" class="field-error">{{ openLine.error.value }}</p>
      </div>

      <IonList lines="none" class="handoff__list">
        <IonItem v-for="(l, i) in lines" :key="l.id" lines="none" class="handoff__row" :class="{ 'is-next': walking && nextLine?.id === l.id }">
          <span slot="start" class="handoff__step" :class="{ 'is-done': opened.has(l.id) }" aria-hidden="true">
            <IonIcon v-if="opened.has(l.id)" :icon="checkmark" />
            <template v-else>{{ i + 1 }}</template>
          </span>
          <IonLabel>
            <h3>{{ displayName(l.coffee.name) }}</h3>
            <p>{{ l.coffee.roaster }} · {{ l.size }} × {{ l.qty }}</p>
          </IonLabel>
          <IonButton
            slot="end"
            :fill="opened.has(l.id) ? 'clear' : 'outline'"
            class="handoff__open"
            :class="{ 'is-done': opened.has(l.id) }"
            :aria-label="opened.has(l.id) ? `Opened ${l.coffee.name}. Open it again` : `Open ${l.coffee.name} on beanhub.com.au`"
            @click="openLine.run(l)"
          >
            {{ opened.has(l.id) ? 'Opened' : 'Open' }}
          </IonButton>
        </IonItem>
      </IonList>

      <p class="handoff__foot">
        Prices, stock and shipping are confirmed by BeanHub at checkout.
      </p>
    </div>
  </IonModal>
</template>

<script>
import { reactive } from 'vue';
// Which lines have been opened, for as long as the app is open: closing and
// reopening the sheet keeps your place.
const opened = reactive(new Set());
// Checked 2026-09-25: /cart is BeanHub's real cart page (200, "Shopping Cart | BeanHub").
const CART_URL = 'https://beanhub.com.au/cart';
</script>

<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import { IonModal, IonButton, IonIcon, IonList, IonItem, IonLabel } from '@ionic/vue';
import { closeOutline, checkmark, checkmarkCircle, openOutline } from 'ionicons/icons';
import { useAction } from '../core/ui/index.js';
import { isNative, plugin } from '../core/native.js';
import { displayName } from './CoffeeLabel.vue';


const props = defineProps({
  open: { type: Boolean, default: false },
  /** `bagLines` */
  lines: { type: Array, default: () => [] },
});
const emit = defineEmits(['close']);

const total = computed(() => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' })
  .format(props.lines.reduce((n, l) => n + l.price * l.qty, 0)));
const walking = ref(false);
const openedCount = computed(() => props.lines.filter((l) => opened.has(l.id)).length);
const allDone = computed(() => props.lines.length > 0 && openedCount.value === props.lines.length);
const nextIndex = computed(() => props.lines.findIndex((l) => !opened.has(l.id)));
const nextLine = computed(() => props.lines[nextIndex.value] || null);

// The in-app browser when the native plugin is there; a new tab otherwise.
const browser = isNative ? plugin('Browser') : null;
const nativeWalk = computed(() => !!browser?.open);

async function openUrl(url) {
  if (browser?.open) {
    try { await browser.open({ url, presentationStyle: 'popover' }); return; } catch { /* plugin missing natively: fall through */ }
  }
  const w = window.open(url, '_blank', 'noopener');
  // `noopener` makes window.open return null even on success, so only a thrown
  // error means it did not open.
  return w;
}

const openLine = useAction(async (line) => {
  await openUrl(line.coffee.url);
  opened.add(line.id);
}, { failed: 'Couldn\'t open BeanHub. Try again.' });

const openCart = useAction(() => openUrl(CART_URL), { failed: 'Couldn\'t open BeanHub. Try again.' });

function startWalk() {
  walking.value = true;
  openNext();
}

async function openNext() {
  if (!nextLine.value) { walking.value = false; return; }
  await openLine.run(nextLine.value);
}

// Native: when the person closes BeanHub's page, open the next one.
let finished = null;
if (browser?.addListener) {
  Promise.resolve(browser.addListener('browserFinished', () => {
    if (!walking.value) return;
    if (nextLine.value) openNext();
    else walking.value = false;
  })).then((h) => { finished = h; }).catch(() => {});
}
onBeforeUnmount(() => { finished?.remove?.(); });

watch(allDone, (d) => { if (d) walking.value = false; });
// Forget lines that have left the bag.
watch(() => props.lines.map((l) => l.id), (ids) => {
  for (const id of [...opened]) if (!ids.includes(id)) opened.delete(id);
});

function onDismiss() {
  walking.value = false;
  emit('close');
}
</script>

<style scoped>
.handoff__sheet {
  height: 100%;
  overflow-y: auto;
  padding: var(--sp-5) var(--gutter) calc(var(--sp-6) + env(safe-area-inset-bottom, 0px));
  background: var(--dock, #FAF7F1);
  color: var(--ink, #1F2A22);
}

.handoff__head { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--sp-3); }
.handoff__kick {
  margin: var(--sp-2) 0 var(--sp-1);
  color: var(--muted, #5E6B60);
  font-family: var(--font-mono, 'Martian Mono', ui-monospace, monospace);
  font-size: calc(var(--text-xs) * 0.9);
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-variant-numeric: tabular-nums;
}
.handoff__title {
  margin: 0;
  font-family: var(--font-display, 'Bricolage Grotesque', 'Manrope', system-ui, sans-serif);
  font-size: var(--text-2xl);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.025em;
}
.handoff__close {
  --color: var(--ink, #1F2A22);
  --background: var(--sage, #E6EDE4);
  --background-hover: var(--sage, #E6EDE4);
  --border-radius: 50%;
  --padding-start: 0;
  --padding-end: 0;
  width: var(--tap);
  height: var(--tap);
  margin: 0;
}
.handoff__sub { margin: var(--sp-3) 0 0; color: var(--ink-2, #3E4A41); font-size: calc(var(--text-md) * 0.9375); line-height: 1.5; }

.handoff__lead { margin: var(--sp-5) 0 var(--sp-2); text-align: center; }

.handoff__primary,
.handoff__open,
.handoff__stop { text-transform: none; letter-spacing: normal; }

/* The one primary: ink pill, 52 tall, dock text. */
.handoff__primary {
  --background: var(--ink, #1F2A22);
  --background-hover: var(--ink, #1F2A22);
  --background-activated: var(--ink, #1F2A22);
  --background-focused: var(--ink, #1F2A22);
  --color: var(--dock, #FAF7F1);
  --border-radius: 999px;
  --box-shadow: none;
  height: calc(var(--sp-7) + var(--sp-1));
  margin: 0;
  font-size: calc(var(--text-md) * 1.0625);
  font-weight: 800;
  transition: transform 120ms var(--ease);
}
.handoff__primary:active { transform: scale(0.97); }
@media (prefers-reduced-motion: reduce) { .handoff__primary, .handoff__primary:active { transform: none; transition: none; } }

.handoff__stop { --color: var(--muted, #5E6B60); margin-top: var(--sp-1); font-weight: 700; }
.handoff__walking { margin: 0 0 var(--sp-3); color: var(--ink, #1F2A22); font-weight: 600; }

.handoff__done {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  margin: 0 0 var(--sp-4);
  padding: var(--sp-3) var(--sp-4);
  border-radius: var(--radius);
  background: var(--sage, #E6EDE4);
  text-align: left;
}
.handoff__done p { margin: 0; color: var(--ink, #1F2A22); font-weight: 700; }
.handoff__done ion-icon { flex: none; color: var(--leaf, #3F7A4E); font-size: var(--text-2xl); }

.handoff__next { margin: var(--sp-2) 0 0; color: var(--muted, #5E6B60); font-size: var(--text-sm); font-weight: 600; }

.handoff__list {
  margin: var(--sp-4) 0 0;
  padding: 0;
  border-radius: var(--radius);
  overflow: hidden;
  background: var(--paper, #F4EFE6);
}
.handoff__row {
  --background: transparent;
  --padding-start: var(--sp-3);
  --inner-padding-end: var(--sp-3);
  --min-height: calc(var(--sp-8) + var(--sp-1));
  transition: background-color var(--dur) var(--ease);
}
.handoff__row + .handoff__row { border-top: 1px solid var(--line, #ECE6DC); }
.handoff__row.is-next { --background: color-mix(in srgb, var(--mustard, #F2C230) 18%, var(--paper, #F4EFE6)); }
.handoff__row h3 {
  color: var(--ink, #1F2A22);
  font-size: calc(var(--text-md) * 0.9375) !important;
  font-weight: 800 !important;
  white-space: normal;
}
.handoff__row p { color: var(--muted, #5E6B60) !important; font-size: var(--text-sm) !important; }

/* Step numbers in the clips' mono; a done step turns leaf with a tick. */
.handoff__step {
  display: grid;
  place-items: center;
  width: calc(var(--sp-6) - var(--sp-1));
  height: calc(var(--sp-6) - var(--sp-1));
  margin-right: var(--sp-3);
  border-radius: 50%;
  background: var(--dock, #FAF7F1);
  box-shadow: inset 0 0 0 1px var(--line, #ECE6DC);
  color: var(--ink, #1F2A22);
  font-family: var(--font-mono, 'Martian Mono', ui-monospace, monospace);
  font-size: var(--text-xs);
  font-weight: 500;
}
.handoff__step.is-done { background: var(--leaf, #3F7A4E); box-shadow: none; color: var(--dock, #FAF7F1); }

.handoff__open {
  --color: var(--ink, #1F2A22);
  --border-color: color-mix(in srgb, var(--ink, #1F2A22) 22%, transparent);
  --border-width: 1px;
  --border-radius: 999px;
  --background-hover: var(--sage, #E6EDE4);
  min-height: var(--tap);
  font-weight: 800;
}
.handoff__open.is-done { --color: var(--leaf, #3F7A4E); }

.handoff__foot { margin: var(--sp-4) 0 0; text-align: center; color: var(--muted, #5E6B60); font-size: var(--text-sm); }
</style>

<style>
ion-modal.handoff { --border-radius: 24px; --background: var(--dock, #FAF7F1); }
ion-modal.handoff::part(handle) { background: color-mix(in srgb, var(--ink, #1F2A22) 22%, transparent); }
</style>
